/**
 * actionItemOverrides — tiny module-level store of "we just wrote this
 * authoritative list to the server" entries, so a stale refetch of
 * /meta (Cloudflare KV is eventually consistent) can't briefly resurrect
 * items we just mutated.
 *
 * Flow:
 *   1. Our /meta/actionItems mutation succeeds → rememberAuthoritative(goalKey, items)
 *   2. Within OVERRIDE_TTL_MS, any meta refetch that returns a DIFFERENT
 *      list for this goalKey is overridden client-side using this authoritative
 *      version.
 *   3. After TTL, the override is dropped — KV has had plenty of time to
 *      propagate and the server response is trusted again.
 *
 * This module uses window.setTimeout directly; React Query's query cache
 * listener (see useActionItems) reads the overrides on each render.
 *
 * This is not a full CRDT — it's a small, time-bounded patch over a known
 * consistency gap. The server remains the source of truth.
 */

const OVERRIDE_TTL_MS = 10_000

const overrides = new Map() // goalKey -> { items, at }

export function rememberAuthoritative(goalKey, items) {
  if (!goalKey || !Array.isArray(items)) return
  overrides.set(goalKey, { items, at: Date.now() })
  // Auto-clear after TTL so we don't hold onto this forever
  setTimeout(() => {
    const entry = overrides.get(goalKey)
    if (entry && Date.now() - entry.at >= OVERRIDE_TTL_MS) {
      overrides.delete(goalKey)
    }
  }, OVERRIDE_TTL_MS + 100)
}

/**
 * Return the authoritative list for goalKey if we have a recent one,
 * otherwise null. Callers should use this to override whatever came
 * back in the meta refetch.
 */
export function getAuthoritative(goalKey) {
  const entry = overrides.get(goalKey)
  if (!entry) return null
  // NaN-safe TTL check — prevents corrupted `entry.at` from holding
  // the override forever (NaN comparisons always return false, so
  // the TTL check would never fire).
  if (!Number.isFinite(entry.at) || Date.now() - entry.at >= OVERRIDE_TTL_MS) {
    overrides.delete(goalKey)
    return null
  }
  return entry.items
}

/**
 * Clear a specific override — rarely needed, but useful if the caller
 * knows the server has caught up (e.g., after explicit success).
 */
export function clearAuthoritative(goalKey) {
  overrides.delete(goalKey)
}
